import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Calendar, Instagram, ChevronRight, MapPin, Phone, Clock } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_SLIDES = [
  { img: "./images/hero1.jpg", label: "Kolory Kreatywne" },
  { img: "./images/hero2.jpg", label: "Rainbow Fusion" },
  { img: "./images/hero3.jpg", label: "Midnight Blue" },
  { img: "./images/hero4.jpg", label: "Honey Balayage" },
  { img: "./images/hero5.jpg", label: "Rose Gold" },
];

const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

export default function Home() {
  const [slide, setSlide] = useState(0);
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    const iv = setInterval(() => setSlide(s => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/pricing`),
      axios.get(`${API}/gallery`),
      axios.get(`${API}/trainings`),
      axios.get(`${API}/partners`),
    ]).then(([s, g, t, p]) => {
      setServices(s.data.slice(0, 4));
      setGallery(g.data.slice(0, 9));
      setTrainings(t.data.slice(0, 3));
      setPartners(p.data);
    }).catch(console.error);
  }, []);

  return (
    <div className="bg-brand-base min-h-screen">
      <Navbar />

      {/* ─── HERO ─────────────────────────────────────────────────────────────── */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
        {/* Slideshow */}
        {HERO_SLIDES.map((s, i) => (
          <div key={i} className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: i === slide ? 1 : 0 }}>
            <img src={s.img} alt={s.label} className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.3) 0%, rgba(5,5,5,0.7) 60%, rgba(5,5,5,1) 100%)" }} />
          </div>
        ))}
        {/* Glow orb */}
        <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full animate-glow-pulse pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(139,0,255,0.25) 0%, transparent 70%)", filter: "blur(60px)" }} />

        {/* Hero content */}
        <div className="relative z-10 max-w-screen-2xl mx-auto px-6 md:px-12 pb-20 md:pb-28 w-full">
          <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}>
            <p className="font-outfit text-xs uppercase tracking-[0.4em] text-violet-400 mb-4">Milena Bachara</p>
            <h1 className="font-anton text-[14vw] sm:text-[10vw] md:text-[9vw] lg:text-[8vw] leading-none text-white uppercase tracking-tight">
              KOLORYZACJA,
            </h1>
            <h1 className="font-anton text-[14vw] sm:text-[10vw] md:text-[9vw] lg:text-[8vw] leading-none uppercase tracking-tight gradient-text">
              KTÓRA
            </h1>
            <h1 className="font-anton text-[14vw] sm:text-[10vw] md:text-[9vw] lg:text-[8vw] leading-none text-white uppercase tracking-tight">
              ROBI WRAŻENIE.
            </h1>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/rezerwacja" data-testid="hero-book-btn" className="btn-primary">
              Zarezerwuj Wizytę <ArrowRight size={14} />
            </Link>
            <Link to="/szkolenia" data-testid="hero-trainings-btn" className="btn-secondary">
              Szkolenia
            </Link>
          </motion.div>

          {/* Slide indicators */}
          <div className="flex gap-2 mt-10">
            {HERO_SLIDES.map((_, i) => (
              <button key={i} data-testid={`hero-slide-${i}`} onClick={() => setSlide(i)}
                className={`h-0.5 transition-all duration-300 ${i === slide ? "w-10 bg-violet-500" : "w-4 bg-white/20"}`} />
            ))}
          </div>
        </div>

        {/* Slide label */}
        <div className="absolute bottom-8 right-8 md:right-12 z-10 hidden md:block">
          <p className="text-white/30 text-xs uppercase tracking-[0.2em]">{HERO_SLIDES[slide].label}</p>
        </div>
      </section>

      {/* ─── ABOUT TEASER ──────────────────────────────────────────────────────── */}
      <section data-testid="about-teaser" className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <FadeUp>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-6">O Artystce</p>
            <h2 className="font-anton text-5xl md:text-6xl text-white uppercase leading-none mb-8">
              MILENA<br /><span className="gradient-text">BACHARA</span>
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-6">
              Jestem fryzjerką, która traktuje włosy jak płótno. Każda koloryzacja to dla mnie projekt artystyczny - unikalna historia napisana farbą na włosach.
            </p>
            <p className="text-white/60 text-base leading-relaxed mb-8">
              Specjalizuję się w zaawansowanej koloryzacji - od subtelnych technik balayage po odważne, kreatywne kolory. Pracuję solo, bo wierzę, że każdy klient zasługuje na pełną uwagę.
            </p>
            <Link to="/o-mnie" data-testid="about-teaser-link" className="btn-primary">
              Poznaj Mnie <ArrowRight size={14} />
            </Link>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="relative">
              <img src="./images/IMG_3793.JPG"
                alt="Milena Bachara" className="w-full object-cover" />
              <div className="absolute inset-0 border border-violet-600/20" />
              {/* Decoration */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border border-violet-600/30" />
              <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-violet-600/10 border border-violet-600/20" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ─── ART STATEMENT ─────────────────────────────────────────────────────── */}
      <section data-testid="art-statement" className="py-24 relative overflow-hidden" style={{ background: "#0A0A0A" }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="glow-orb w-64 h-64 left-1/4 top-0 opacity-20" style={{ background: "radial-gradient(circle, rgba(139,0,255,0.6) 0%, transparent 70%)" }} />
          <div className="glow-orb w-48 h-48 right-1/4 bottom-0 opacity-20" style={{ background: "radial-gradient(circle, rgba(59,91,255,0.6) 0%, transparent 70%)" }} />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 relative">
          <FadeUp>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-8 text-center">Filozofia</p>
            <h2 className="font-anton text-5xl sm:text-6xl md:text-[6vw] text-white uppercase leading-none text-center">
              KOLORYZACJA<br /><span className="gradient-text">JAKO SZTUKA</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.2} className="max-w-2xl mx-auto text-center mt-8">
            <p className="text-white/50 text-lg leading-relaxed">
              Włosy to jedyne dzieło sztuki, które nosisz ze sobą codziennie. Każdy kolor jest przemyślany, każda technika dobrana pod Ciebie - bo zasługujesz na coś więcej niż fryzjer z szablonu.
            </p>
          </FadeUp>

          {/* Stats */}
          <FadeUp delay={0.4} className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16">
            {[
              { n: "1500+", l: "Metamorfoz" },
              { n: "10", l: "Lat doświadczenia" },
              { n: "50+", l: "Szkoleń" },
              { n: "100%", l: "Zaangażowania" },
            ].map((s, i) => (
              <div key={i} className="text-center border border-white/5 p-6">
                <div className="font-anton text-4xl gradient-text">{s.n}</div>
                <div className="text-white/40 text-xs uppercase tracking-[0.2em] mt-2">{s.l}</div>
              </div>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* ─── SERVICES PREVIEW ──────────────────────────────────────────────────── */}
      <section data-testid="services-preview" className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12">
        <FadeUp className="flex justify-between items-end mb-4">
          <div>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Oferta</p>
            <h2 className="font-anton text-5xl text-white uppercase">USŁUGI</h2>
          </div>
          <Link to="/uslugi" data-testid="services-all-link" className="text-white/40 hover:text-white text-sm uppercase tracking-[0.15em] flex items-center gap-2 transition-colors">
            Wszystkie <ChevronRight size={14} />
          </Link>
        </FadeUp>

        {/* Editorial numbered list */}
        <div className="divide-y divide-white/5 mt-8">
          {services.map((s, i) => (
            <FadeUp key={s.id} delay={i * 0.08}>
              <Link to="/rezerwacja" data-testid={`service-home-book-${i}`}
                className="flex items-center gap-6 md:gap-10 py-7 group hover:bg-white/[0.02] transition-all duration-300 -mx-2 px-2 cursor-pointer block">

                {/* Big number */}
                <div className="font-anton text-5xl md:text-6xl leading-none flex-shrink-0 w-12 text-right transition-colors duration-300"
                  style={{ WebkitTextStroke: "1px rgba(139,0,255,0.2)", color: "transparent" }}
                  onMouseEnter={e => e.currentTarget.style.WebkitTextStroke = "1px rgba(139,0,255,0.5)"}
                  onMouseLeave={e => e.currentTarget.style.WebkitTextStroke = "1px rgba(139,0,255,0.2)"}>
                  {String(i + 1).padStart(2, "0")}
                </div>

                {/* Divider */}
                <div className="w-px h-10 bg-white/8 flex-shrink-0 hidden md:block" />

                {/* Name + meta */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-anton text-xl md:text-2xl lg:text-3xl text-white uppercase leading-tight group-hover:text-violet-300 transition-colors duration-300 truncate">
                    {s.subtitle}
                  </h3>
                  <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                    <span className="text-white/30 text-xs font-outfit flex items-center gap-1">
                      <Clock size={10} className="inline" />
                      {s.durations?.[0] || 60}–{s.durations?.[s.durations?.length - 1] || 60} min
                    </span>
                    <span className="text-white/20 text-xs font-outfit">{s.rows?.length || 0} wariantów</span>
                  </div>
                </div>

                {/* Price */}
                <div className="text-right flex-shrink-0">
                  <div className="text-white/25 text-[10px] uppercase tracking-[0.15em] font-outfit">od</div>
                  <div className="font-anton text-xl md:text-2xl gradient-text leading-tight">{s.rows?.[0]?.price || "—"}</div>
                </div>

                {/* Arrow */}
                <ArrowRight size={16} className="text-violet-500/30 group-hover:text-violet-400 flex-shrink-0 transition-all duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.4} className="mt-10 flex justify-center">
          <Link to="/rezerwacja" data-testid="services-book-btn" className="btn-primary">
            Zarezerwuj Wizytę <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </section>

      {/* ─── GALLERY PREVIEW ───────────────────────────────────────────────────── */}
      <section data-testid="gallery-preview" className="py-24" style={{ background: "#0A0A0A" }}>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <FadeUp className="flex justify-between items-end mb-12">
            <div>
              <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Portfolio</p>
              <h2 className="font-anton text-5xl text-white uppercase">TRANSFORMACJE</h2>
            </div>
            <Link to="/galeria" data-testid="gallery-all-link" className="text-white/40 hover:text-white text-sm uppercase tracking-[0.15em] flex items-center gap-2 transition-colors">
              Galeria <ChevronRight size={14} />
            </Link>
          </FadeUp>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
            {gallery.slice(0, 9).map((g, i) => (
              <FadeUp key={g.id} delay={i * 0.06}>
                <div className="img-hover-overlay aspect-square overflow-hidden cursor-pointer">
                  <img src={g.image_url} alt={g.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── UPCOMING TRAININGS ────────────────────────────────────────────────── */}
      <section data-testid="trainings-preview" className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12">
        <FadeUp className="flex justify-between items-end mb-12">
          <div>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Edukacja</p>
            <h2 className="font-anton text-5xl text-white uppercase">SZKOLENIA</h2>
          </div>
          <Link to="/szkolenia" data-testid="trainings-all-link" className="text-white/40 hover:text-white text-sm uppercase tracking-[0.15em] flex items-center gap-2 transition-colors">
            Wszystkie <ChevronRight size={14} />
          </Link>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {trainings.map((t, i) => (
            <FadeUp key={t.id} delay={i * 0.15}>
              <Link to="/szkolenia" data-testid={`training-card-${i}`} className="block">
              <div className="border border-white/8 p-4 sm:p-6 md:p-8 card-hover group cursor-pointer h-full" style={{ background: "#0A0A0A" }}>
                <div className="flex justify-between items-start mb-4 gap-2">
                  <div className="text-xs font-outfit text-violet-400 border border-violet-600/30 px-2 py-1 flex-shrink-0">
                    {t.is_online ? "Online" : "Stacjonarne"}
                  </div>
                  <div className="text-xs text-white/30 flex-shrink-0">{t.duration}h</div>
                </div>
                <h3 className="font-anton text-xl md:text-2xl text-white uppercase leading-tight mb-3 group-hover:text-violet-300 transition-colors">{t.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed mb-4 line-clamp-2">{t.description}</p>
                <div className="flex justify-between items-end pt-4 border-t border-white/5">
                  <div>
                    <div className="text-white/40 text-xs uppercase tracking-[0.12em]">Cena</div>
                    <div className="font-anton text-xl md:text-2xl text-white">{t.price} zł</div>
                  </div>
                  <div className="text-right">
                    <div className="text-white/40 text-xs uppercase tracking-[0.12em]">Pozostało</div>
                    <div className={`font-outfit text-sm font-medium ${t.spots_available <= 2 ? "text-red-400" : "text-green-400"}`}>
                      {t.spots_available}/{t.spots_total}
                    </div>
                  </div>
                </div>
                <div className="btn-primary w-full text-center justify-center mt-5 text-xs">
                  Kup Szkolenie <ArrowRight size={12} />
                </div>
              </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ─── INSTAGRAM ─────────────────────────────────────────────────────────── */}
      <section data-testid="instagram-section" className="py-24" style={{ background: "#0A0A0A" }}>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <FadeUp className="text-center mb-12">
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Social Media</p>
            <h2 className="font-anton text-5xl text-white uppercase">INSTAGRAM</h2>
            <a href="https://www.instagram.com/fryzjer_artysta/" target="_blank" rel="noopener noreferrer"
              data-testid="instagram-link" className="inline-flex items-center gap-2 mt-4 text-white/40 hover:text-white text-sm uppercase tracking-[0.15em] transition-colors">
              <Instagram size={16} /> @fryzjer_artysta
            </a>
          </FadeUp>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-1">
            {gallery.filter(g => g.is_instagram).slice(0, 6).map((g, i) => (
              <FadeUp key={g.id} delay={i * 0.05}>
                <a href="https://www.instagram.com/fryzjer_artysta/" target="_blank" rel="noopener noreferrer"
                  data-testid={`instagram-item-${i}`} className="block relative group aspect-square overflow-hidden">
                  <img src={g.image_url} alt={g.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-violet-600/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Instagram size={24} className="text-white" />
                  </div>
                </a>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PARTNERS ──────────────────────────────────────────────────────────── */}
      {partners.length > 0 && (
        <section data-testid="partners-section" className="py-20 border-y border-white/5">
          <FadeUp className="text-center mb-10">
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-white/30">Współpracuję z</p>
          </FadeUp>
          <div className="marquee-container">
            <div className="marquee-content gap-16 px-8">
              {[...partners, ...partners].map((p, i) => (
                <div key={i} className="flex-shrink-0 px-8 py-2">
                  <span className="font-anton text-xl uppercase tracking-widest text-white/20 hover:text-white/60 transition-colors whitespace-nowrap">
                    {p.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── BOOKING CTA ───────────────────────────────────────────────────────── */}
      <section data-testid="booking-cta" className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div style={{ background: "radial-gradient(circle at 50% 50%, rgba(139,0,255,0.15) 0%, transparent 70%)" }} className="absolute inset-0" />
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 text-center">
          <FadeUp>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-6">Gotowa na zmianę?</p>
            <h2 className="font-anton text-6xl md:text-8xl text-white uppercase leading-none mb-8">
              WŁOSY JAK<br /><span className="gradient-text">DZIEŁO SZTUKI.</span>
            </h2>
            <p className="text-white/50 text-lg mb-10 max-w-lg mx-auto">
              Zarezerwuj wizytę i odkryj koloryzację, która naprawdę robi wrażenie.
            </p>
            <Link to="/rezerwacja" data-testid="cta-book-btn" className="btn-primary text-base px-10 py-5">
              Zarezerwuj Teraz <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ─── CONTACT TEASER ────────────────────────────────────────────────────── */}
      <section data-testid="contact-teaser" className="py-16 border-t border-white/5 max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: <Phone size={18} />, label: "Zadzwoń", value: "+48 798 943 906" },
            { icon: <MapPin size={18} />, label: "Znajdź mnie", value: "Tarnów, ul. Reymonta 25" },
            { icon: <Calendar size={18} />, label: "Godziny", value: "Pon–Sob: 8:00–19:00" },
          ].map((c, i) => (
            <FadeUp key={i} delay={i * 0.1} className="flex items-center gap-4">
              <div className="text-violet-500 border border-violet-600/30 p-3">{c.icon}</div>
              <div>
                <div className="text-white/30 text-xs uppercase tracking-[0.2em]">{c.label}</div>
                <div className="text-white text-sm mt-1">{c.value}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
